import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import VerificationStatusCard from '@/components/VerificationStatusCard';
import { Scale, Shield, FileText, ArrowRight, MapPin, CheckCircle2, Clock, AlertTriangle, User as UserIcon, Mail, X, Check, Inbox } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LawyerDashboardPage() {
  const { language } = useLanguage();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [txs, setTxs] = useState([]);
  const [invitations, setInvitations] = useState({ pending: [], history: [] });
  const [respondingId, setRespondingId] = useState('');
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };
  const tt = (en, es) => language === 'es' ? es : en;

  const STATUS_ES = {
    pending: 'Pendiente', in_progress: 'En Progreso', completed: 'Completado',
    requested: 'Solicitado', scheduled: 'Programado', submitted: 'Enviado',
    profile_incomplete: 'Perfil Incompleto', profile_submitted: 'Perfil Enviado',
    profile_verified: 'Perfil Verificado', poa_missing: 'Poder Faltante',
    poa_uploaded: 'Poder Subido', poa_verified: 'Poder Verificado',
    draft_uploaded: 'Borrador Subido', under_review: 'En Revisión',
    changes_requested: 'Cambios Solicitados', approved: 'Aprobado',
    accepted: 'Aceptada', rejected: 'Rechazada', proof_uploaded: 'Comprobante Subido',
    confirmed: 'Confirmado', registered: 'Registrado',
  };
  const ts = (status) => {
    if (!status) return '';
    if (language === 'es') return STATUS_ES[status] || status.replace(/_/g, ' ');
    return status.replace(/_/g, ' ');
  };

  const fetchData = () => {
    if (user?.role !== 'lawyer') { setLoading(false); return; }
    axios.get(`${API}/lawyer/dashboard`, { headers })
      .then(r => setData(r.data))
      .catch(() => toast.error(tt('Failed to load dashboard', 'Error al cargar panel')))
      .finally(() => setLoading(false));
    axios.get(`${API}/transactions/my`, { headers })
      .then(r => setTxs(r.data.transactions || []))
      .catch(() => {});
    axios.get(`${API}/lawyer/invitations`, { headers })
      .then(r => setInvitations({ pending: r.data.pending || [], history: r.data.history || [] }))
      .catch(() => {});
  };

  useEffect(() => { fetchData(); }, [user]);

  const respondInvitation = async (invId, action) => {
    setRespondingId(invId);
    try {
      const res = await axios.post(`${API}/lawyer/invitations/${invId}/${action}`, {}, { headers });
      toast.success(action === 'accept'
        ? tt('Invitation accepted!', '¡Invitación aceptada!')
        : tt('Invitation declined', 'Invitación rechazada'));
      fetchData();
      if (action === 'accept' && res.data?.tx_id) {
        navigate(`/transaction/${res.data.tx_id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || tt('Failed', 'Error'));
    } finally {
      setRespondingId('');
    }
  };

  if (user?.role !== 'lawyer') {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center" data-testid="no-lawyer-access">
        <Scale className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h2 className="font-heading text-xl font-semibold text-slate-700 mb-2">
          {tt('Lawyer Access Required', 'Se Requiere Acceso de Abogado')}
        </h2>
        <p className="text-slate-500">
          {tt('This dashboard is for verified lawyers only.', 'Este panel es solo para abogados verificados.')}
        </p>
      </div>
    );
  }

  if (loading) return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-4">
      <Skeleton className="h-10 w-64" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
    </div>
  );

  const profileCompletion = data?.profile_completion || {};
  const stageLabels = {
    tour: tt('Virtual Tour', 'Tour Virtual'), onboarding: tt('Documents', 'Documentos'),
    poa: tt('POA', 'Poder'), due_diligence: tt('Due Diligence', 'Debida Diligencia'),
    offer: tt('Offer', 'Oferta'), contract: tt('Contract', 'Contrato'),
    payment: tt('Payment', 'Pago'), signing: tt('Signing', 'Firma'),
    registry: tt('Registry', 'Registro'), complete: tt('Complete', 'Completo'),
    delivery: tt('Delivery', 'Entrega'),
  };

  const statCards = [
    { label: tt('Invitations', 'Invitaciones'), value: invitations.pending.length, icon: Inbox, color: 'text-fuchsia-600 bg-fuchsia-50', testid: 'stat-pending-invitations' },
    { label: tt('Active Cases', 'Casos Activos'), value: data?.active_transactions || 0, icon: FileText, color: 'text-blue-600 bg-blue-50', testid: 'stat-active-cases' },
    { label: tt('Action Needed', 'Acción Requerida'), value: data?.action_needed_count || 0, icon: AlertTriangle, color: 'text-amber-600 bg-amber-50', testid: 'stat-action-needed' },
    { label: tt('Completed', 'Completadas'), value: data?.completed_transactions || 0, icon: CheckCircle2, color: 'text-green-600 bg-green-50', testid: 'stat-completed' },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="lawyer-dashboard-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3">
            <Scale className="w-7 h-7 text-[#0B192C]" />
            <h1 className="font-heading text-2xl md:text-3xl font-semibold text-slate-900">
              {tt('Lawyer Dashboard', 'Panel del Abogado')}
            </h1>
            {data?.verified ? (
              <Badge className="bg-emerald-100 text-emerald-700 text-xs gap-1" data-testid="lawyer-verified-badge">
                <Shield className="w-3 h-3" /> {tt('Verified', 'Verificado')}
              </Badge>
            ) : (
              <Badge className="bg-slate-100 text-slate-600 text-xs">
                {tt('Unverified', 'No verificado')}
              </Badge>
            )}
          </div>
          <p className="text-sm text-slate-500 mt-1 ml-10">
            {tt('Manage profile, verification and tracked transactions.', 'Gestiona tu perfil, verificación y transacciones acompañadas.')}
          </p>
        </div>
      </div>

      {/* Verification CTA (only if not verified) */}
      {!data?.verified && (
        <div className="border-2 border-[#D4AF37]/40 bg-gradient-to-br from-amber-50 to-white rounded-xl p-4 mb-5 flex items-center justify-between gap-4 flex-wrap" data-testid="lawyer-verify-cta">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 text-[#0B192C]" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-[#0B192C]">
                {tt('Get verified to receive case invitations', 'Verifícate para recibir invitaciones')}
              </p>
              <p className="text-xs text-slate-600 mt-0.5">
                {tt('Only verified lawyers appear in the public directory and can be invited to tracked transactions.', 'Solo los abogados verificados aparecen en el directorio público y pueden ser invitados a transacciones acompañadas.')}
              </p>
            </div>
          </div>
          <Link to="/verification">
            <Button className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C]" data-testid="lawyer-start-verify-btn">
              {tt('Start verification →', 'Iniciar verificación →')}
            </Button>
          </Link>
        </div>
      )}

      {/* Profile / Verification cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        {/* Profile completion */}
        <Link to="/profile" className="block group" data-testid="lawyer-profile-completion-link">
          <Card className="transition-all hover:border-[#D4AF37] hover:shadow-md cursor-pointer h-full" data-testid="lawyer-profile-completion-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <UserIcon className="w-4 h-4 text-primary" />
                  <p className="font-semibold text-sm">{tt('Profile Completion', 'Perfil Completo')}</p>
                </div>
                <span className="font-heading font-bold text-lg text-primary" data-testid="lawyer-profile-pct">
                  {profileCompletion.percentage || 0}%
                </span>
              </div>
              <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden mb-2">
                <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${profileCompletion.percentage || 0}%` }} />
              </div>
              {profileCompletion.missing?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {profileCompletion.missing.map(field => (
                    <span key={field} className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200 capitalize">
                      {field === 'verified' ? tt('Get Verified', 'Verificarse') : field.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              )}
              {profileCompletion.percentage === 100 ? (
                <p className="text-xs text-green-600">{tt('Profile complete!', '¡Perfil completo!')}</p>
              ) : (
                <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-[#0B192C] group-hover:text-[#D4AF37] transition">
                  {tt('Complete profile →', 'Completar perfil →')}
                </div>
              )}
            </CardContent>
          </Card>
        </Link>

        {/* Verification */}
        <VerificationStatusCard />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="lawyer-stats">
        {statCards.map((s, i) => (
          <Card key={i} data-testid={s.testid}>
            <CardContent className="p-3 flex items-center gap-2.5">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${s.color}`}>
                <s.icon className="w-4 h-4" />
              </div>
              <div>
                <div className="font-heading text-lg font-bold">{s.value}</div>
                <div className="text-[11px] text-slate-500 leading-tight">{s.label}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue={invitations.pending.length > 0 ? 'invitations' : 'transactions'} className="space-y-4">
        <TabsList className="bg-slate-100 p-1 rounded-xl">
          <TabsTrigger value="invitations" className="rounded-lg gap-1.5" data-testid="lawyer-tab-invitations">
            <Inbox className="w-4 h-4" />{tt('Invitations', 'Invitaciones')}
            {invitations.pending.length > 0 && <Badge className="ml-1 bg-fuchsia-500 text-white text-xs h-5 px-1.5">{invitations.pending.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="transactions" className="rounded-lg gap-1.5" data-testid="lawyer-tab-transactions">
            <FileText className="w-4 h-4" />{tt('Transactions', 'Transacciones')}
            {txs.length > 0 && <Badge className="ml-1 bg-primary text-white text-xs h-5 px-1.5">{txs.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="profile" className="rounded-lg gap-1.5" data-testid="lawyer-tab-profile" onClick={() => navigate('/profile')}>
            <UserIcon className="w-4 h-4" />{tt('Profile', 'Perfil')}
          </TabsTrigger>
        </TabsList>

        {/* ===== INVITATIONS TAB ===== */}
        <TabsContent value="invitations">
          {invitations.pending.length > 0 ? (
            <div className="space-y-3" data-testid="pending-invitations-list">
              {invitations.pending.map(inv => {
                const prop = inv.property || {};
                const inviter = inv.inviter || {};
                const inviterLabel = inv.invited_by_role === 'buyer' ? tt('Buyer', 'Comprador')
                  : inv.invited_by_role === 'agent' ? tt('Agent', 'Agente')
                  : tt('Admin', 'Admin');
                const expiresAt = inv.expires_at ? new Date(inv.expires_at) : null;
                const hoursLeft = expiresAt ? Math.max(0, Math.round((expiresAt - Date.now()) / 3600000)) : null;
                return (
                  <Card key={inv.id} className="border-fuchsia-200 bg-fuchsia-50/30" data-testid={`invitation-${inv.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4 flex-wrap">
                        <div className="w-14 h-12 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                          {prop.main_photo ? (
                            <img
                              src={typeof prop.main_photo === 'string' && prop.main_photo.startsWith('http') ? prop.main_photo : `${process.env.REACT_APP_BACKEND_URL}/api/storage/${prop.main_photo}`}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : <div className="w-full h-full bg-slate-200" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-sm truncate">{prop.title || tt('Property', 'Propiedad')}</h4>
                          <p className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{prop.city || '-'}</p>
                          <p className="text-xs text-slate-500 mt-1">
                            {tt('Invited by', 'Invitado por')} <strong>{inviter.name || inviter.email}</strong>
                            {' '}<Badge variant="outline" className="text-[10px] ml-1">{inviterLabel}</Badge>
                          </p>
                          {inv.message && (
                            <div className="mt-2 p-2 bg-white rounded border border-fuchsia-100 text-xs text-slate-700">
                              <Mail className="w-3 h-3 inline mr-1 text-fuchsia-500" />{inv.message}
                            </div>
                          )}
                          {hoursLeft != null && (
                            <p className="text-[10px] text-amber-600 mt-2 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {hoursLeft > 0
                                ? tt(`Expires in ${hoursLeft}h`, `Expira en ${hoursLeft}h`)
                                : tt('Expiring soon', 'Expira pronto')}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2 ml-auto">
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1 text-xs border-slate-300"
                            disabled={respondingId === inv.id}
                            onClick={() => respondInvitation(inv.id, 'decline')}
                            data-testid={`invitation-decline-${inv.id}`}
                          >
                            <X className="w-3.5 h-3.5" />{tt('Decline', 'Rechazar')}
                          </Button>
                          <Button
                            size="sm"
                            className="gap-1 text-xs bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C]"
                            disabled={respondingId === inv.id}
                            onClick={() => respondInvitation(inv.id, 'accept')}
                            data-testid={`invitation-accept-${inv.id}`}
                          >
                            <Check className="w-3.5 h-3.5" />{tt('Accept', 'Aceptar')}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}

              {invitations.history.length > 0 && (
                <div className="mt-6">
                  <p className="text-xs font-semibold text-slate-400 uppercase mb-2">{tt('Recent history', 'Historial reciente')}</p>
                  <div className="space-y-2">
                    {invitations.history.slice(0, 5).map(inv => {
                      const statusColor = inv.status === 'accepted' ? 'text-green-600 bg-green-50'
                        : inv.status === 'declined' ? 'text-slate-500 bg-slate-100'
                        : inv.status === 'expired' ? 'text-amber-600 bg-amber-50'
                        : 'text-slate-500 bg-slate-100';
                      return (
                        <div key={inv.id} className="flex items-center gap-3 p-2.5 bg-white rounded-lg border border-border text-xs" data-testid={`invitation-history-${inv.id}`}>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-700 truncate">{inv.property?.title || tt('Property', 'Propiedad')}</p>
                            <p className="text-[10px] text-slate-400">{tt('From', 'De')} {inv.inviter?.name || inv.inviter?.email}</p>
                          </div>
                          <Badge className={`text-[10px] capitalize ${statusColor}`}>{inv.status}</Badge>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-xl border border-border" data-testid="invitations-empty">
              <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">{tt('No pending invitations', 'No hay invitaciones pendientes')}</p>
              <p className="text-xs text-slate-400 mt-1">
                {data?.verified
                  ? tt('Buyers and agents will invite you when they need a lawyer.', 'Compradores y agentes te invitarán cuando necesiten un abogado.')
                  : tt('Complete verification to be eligible for invitations.', 'Completa la verificación para ser elegible para invitaciones.')}
              </p>
            </div>
          )}
        </TabsContent>

        {/* ===== TRANSACTIONS TAB ===== */}
        <TabsContent value="transactions">
          {txs.length > 0 ? (
            <div className="space-y-3">
              {txs.map(tx => {
                const prop = tx.property || {};
                const stage = tx.current_stage;
                const stageStatus = tx.stages?.[stage]?.status || '';
                const needsAction = (
                  (stage === 'poa' && stageStatus === 'poa_uploaded') ||
                  (stage === 'due_diligence' && ['pending', 'in_progress'].includes(stageStatus)) ||
                  (stage === 'contract' && stageStatus === 'draft_uploaded')
                );
                return (
                  <Link key={tx.id} to={`/transaction/${tx.id}`} data-testid={`lawyer-tx-${tx.id}`}>
                    <Card className={`hover:shadow-md transition-shadow cursor-pointer mb-2 ${needsAction ? 'border-amber-300 bg-amber-50/30' : ''}`}>
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-14 h-12 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                          {prop.main_photo ? (
                            <img
                              src={typeof prop.main_photo === 'string' && prop.main_photo.startsWith('http') ? prop.main_photo : `${process.env.REACT_APP_BACKEND_URL}/api/storage/${prop.main_photo}`}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : <div className="w-full h-full bg-slate-200" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-sm truncate">{prop.title || tt('Property', 'Propiedad')}</h4>
                          <p className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{prop.city}</p>
                          <p className="text-xs text-slate-400 mt-0.5">{tt('Buyer', 'Comprador')}: {tx.buyer?.name || tx.buyer?.email || '-'}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <Badge variant="outline" className="text-[10px] mb-1">{stageLabels[stage] || stage}</Badge>
                          {needsAction ? (
                            <Badge className="block text-[10px] bg-amber-500 text-white">{tt('Action Needed', 'Acción Requerida')}</Badge>
                          ) : (
                            <Badge className="block text-[10px] bg-slate-100 text-slate-500 capitalize">{ts(stageStatus)}</Badge>
                          )}
                        </div>
                        <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-xl border border-border" data-testid="lawyer-tx-empty">
              <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">{tt('No active transactions', 'No hay transacciones activas')}</p>
              <p className="text-xs text-slate-400 mt-1">
                {data?.verified
                  ? tt('Buyers and agents will invite you to tracked transactions from their dashboards.', 'Compradores y agentes te invitarán a transacciones desde sus paneles.')
                  : tt('Complete verification to be eligible for invitations.', 'Completa la verificación para ser elegible para invitaciones.')}
              </p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
